import { api } from '../middleware/api';
import { sseClient } from '../../services/sseClient';

export interface HKPublicPlace {
  pl_id?: number;
  pl_company: string;
  pl_name: string;
  pl_code?: string;
  pl_type: string;
  pl_floor?: string;
  pl_status?: string; // 'C' | 'D' | 'I' | 'M'
  pl_cleaning_freq_hrs?: number;
  pl_last_cleaned_at?: string;
  pl_last_cleaned_by?: number;
  cleaner_name?: string;
  hours_since_cleaned?: number;
  is_overdue?: number;
  pl_checklist?: string;
  pl_flag?: string;
}

export interface HKPublicPlaceSummary {
  total: number;
  clean_count: number;
  dirty_count: number;
  in_progress_count: number;
  maintenance_count: number;
  overdue_count: number;
}

export const hkpublicplaceApi = api
  .enhanceEndpoints({ addTagTypes: ['HKPublicPlace'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getPublicPlaces: builder.query<HKPublicPlace[], string>({
        query: (cid) => `hkpublicplace/${cid}`,
        providesTags: ['HKPublicPlace'],
        async onCacheEntryAdded(
          _arg,
          { cacheDataLoaded, cacheEntryRemoved, dispatch }
        ) {
          try {
            await cacheDataLoaded;
            const unsubscribe = sseClient.addListener((data) => {
              if (
                data.type === 'FRONT_OFFICE_UPDATED' ||
                data.type === 'PUBLIC_CLEANING_UPDATED'
              ) {
                dispatch(
                  hkpublicplaceApi.util.invalidateTags(['HKPublicPlace'])
                );
              }
            });
            await cacheEntryRemoved;
            unsubscribe();
          } catch {
            // no-op
          }
        }
      }),
      getPublicPlacesSummary: builder.query<HKPublicPlaceSummary, string>({
        query: (cid) => `hkpublicplace/${cid}/summary`,
        providesTags: ['HKPublicPlace']
      }),
      getPublicPlaceById: builder.query<HKPublicPlace, number | string>({
        query: (id) => `hkpublicplace/byid/${id}`,
        providesTags: ['HKPublicPlace']
      }),
      insertPublicPlace: builder.mutation<HKPublicPlace, Partial<HKPublicPlace>>({
        query: (body) => ({
          url: 'hkpublicplace/',
          method: 'POST',
          body
        }),
        invalidatesTags: ['HKPublicPlace']
      }),
      updatePublicPlace: builder.mutation<HKPublicPlace, Partial<HKPublicPlace>>({
        query: (body) => ({
          url: `hkpublicplace/${body.pl_id}`,
          method: 'PUT',
          body
        }),
        invalidatesTags: ['HKPublicPlace']
      }),
      deletePublicPlace: builder.mutation<void, number>({
        query: (id) => ({
          url: `hkpublicplace/${id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['HKPublicPlace']
      }),
      seedDefaultPlaces: builder.mutation<{ message: string }, string>({
        query: (cid) => ({
          url: `hkpublicplace/${cid}/seed-defaults`,
          method: 'POST'
        }),
        invalidatesTags: ['HKPublicPlace']
      })
    }),
    overrideExisting: true
  });

export const {
  useGetPublicPlacesQuery,
  useGetPublicPlacesSummaryQuery,
  useGetPublicPlaceByIdQuery,
  useInsertPublicPlaceMutation,
  useUpdatePublicPlaceMutation,
  useDeletePublicPlaceMutation,
  useSeedDefaultPlacesMutation
} = hkpublicplaceApi;
